import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import { Box, Button } from "@mui/material";
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface ChatScrollAnchorHandle {
  /**
   * Capture the current scroll geometry so the layout effect can re-anchor
   * the viewport to the same logical content after a prepend (load-older
   * cycle) changes scrollHeight. Without this the viewport jumps to
   * wherever the new total height puts the existing scrollTop, which is
   * jarring for long histories.
   */
  captureForPrepend: () => void;
  /**
   * Suppress the next unseen-content update. Older-history prepends grow
   * `messages.length`, which would otherwise trip the unseen-content
   * effect into showing the "Jump to latest" pill even though the new
   * content is BEHIND the viewport, not ahead of it.
   */
  suppressNextUnseen: () => void;
}

interface ChatScrollAnchorProps {
  messagesLength: number;
  streamingContent: string;
  isStreaming: boolean;
  sessionId: string | null;
  children: ReactNode;
}

export const ChatScrollAnchor = forwardRef<
  ChatScrollAnchorHandle,
  ChatScrollAnchorProps
>(function ChatScrollAnchor(
  { messagesLength, streamingContent, isStreaming, sessionId, children },
  ref,
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [hasUnseen, setHasUnseen] = useState(false);
  const lastMessageCountRef = useRef(messagesLength);
  // When the user clicks "Load older", we capture the scroll geometry so
  // the layout effect below can re-anchor the viewport to the same
  // logical content after the prepended slice changes scrollHeight.
  // Without this the viewport jumps to wherever the new total height
  // puts the existing scrollTop, which is jarring for long histories.
  const pendingPrependRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  // Older-history prepends grow `messages.length`, which would otherwise
  // trip the unseen-content effect into showing the "Jump to latest"
  // pill even though the new content is BEHIND the viewport, not ahead
  // of it. This one-shot suppresses the next unseen update so the pill
  // stays quiet across a load-older cycle.
  const suppressUnseenRef = useRef(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 64;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    setAtBottom(distance <= threshold);
  }, []);

  // Auto-scroll only when already anchored to the bottom, so users can read
  // earlier output mid-stream without being yanked back down.
  useEffect(() => {
    if (messagesLength === 0 && !streamingContent) return;
    if (atBottom) scrollToBottom("smooth");
  }, [messagesLength, streamingContent, atBottom, scrollToBottom]);

  // Track whether new content has arrived while the user is scrolled away.
  // The "Jump to latest" pill should only nag when there's something to see.
  useEffect(() => {
    const grew = messagesLength > lastMessageCountRef.current;
    lastMessageCountRef.current = messagesLength;
    // Skip exactly once after a "Load older" so the prepended slice
    // doesn't get mistaken for new tail content.
    if (suppressUnseenRef.current) {
      suppressUnseenRef.current = false;
      return;
    }
    if (atBottom) {
      setHasUnseen(false);
    } else if (grew || isStreaming) {
      setHasUnseen(true);
    }
  }, [messagesLength, isStreaming, atBottom]);

  // Restore the viewport after older history is prepended. Runs in a
  // layout effect so the user never sees a flicker between the new DOM
  // and the corrected scroll position.
  useLayoutEffect(() => {
    const anchor = pendingPrependRef.current;
    if (!anchor) return;
    pendingPrependRef.current = null;
    const el = scrollRef.current;
    if (!el) return;
    const delta = el.scrollHeight - anchor.scrollHeight;
    if (delta > 0) {
      el.scrollTop = anchor.scrollTop + delta;
    }
  }, [messagesLength]);

  // When switching into a loaded session, snap to bottom immediately.
  useEffect(() => {
    if (sessionId === null) return;
    setAtBottom(true);
    setHasUnseen(false);
    scrollToBottom("auto");
  }, [sessionId, scrollToBottom]);

  useImperativeHandle(
    ref,
    () => ({
      captureForPrepend: () => {
        const el = scrollRef.current;
        if (el) {
          pendingPrependRef.current = {
            scrollHeight: el.scrollHeight,
            scrollTop: el.scrollTop,
          };
        }
      },
      suppressNextUnseen: () => {
        suppressUnseenRef.current = true;
      },
    }),
    [],
  );

  return (
    <>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          position: "absolute",
          inset: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </Box>

      {!atBottom && hasUnseen && (
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => scrollToBottom("smooth")}
            startIcon={<ArrowDownwardIcon fontSize="small" />}
            sx={{
              pointerEvents: "auto",
              borderRadius: 999,
              textTransform: "none",
              px: 1.75,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: 3,
            }}
          >
            Jump to latest
          </Button>
        </Box>
      )}
    </>
  );
});
